import {useAuth} from '@apps-common/auth-react';
import {parseReference} from '@apps-common/geocorn-sdk/api/util.js';
import Map from '@apps-common/ui-maps/Map.jsx';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
} from '@mui/material';
import {pink, yellow} from '@mui/material/colors';
import Overlay from '@planet/maps/Overlay.js';
import {array, func, object, oneOfType} from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import CollectionLayer from '../Viewer/Map/CollectionLayer.jsx';
import ReservationsOverlay from './ReservationsOverlay.jsx';
import {useGetCollection} from '@api/collections.js';
import {getHashFromId} from '@app/util/granero.js';
import ChipAndLabel from '@components/common/ChipAndLabel.jsx';
import ZoomToButton from '@components/common/ZoomToButton.jsx';

const accessibilityZoneStyle = [
  {
    'stroke-color': yellow[500],
    'stroke-width': 2,
    'fill-color': `color-mix(in srgb, color-mix(in srgb, ${yellow[500]}, white 40%), transparent 90%)`,
    'stroke-line-dash': [5, 5],
  },
];

const QuotaManagerMap = ({
  collection,
  selectedFeatureRefs = [],
  zoomFeature,
  onZoomToFeature,
  selectedStyle,
  baseStyle,
  products,
  reservations,
  selectedProduct,
}) => {
  const mapRef = useRef();
  const {user} = useAuth();

  const [popupElement, setPopupElement] = useState(null);
  const [popupPosition, setPopupPosition] = useState(null);
  const [overlayProducts, setSelectedOverlayProducts] = useState([]);

  const collectionExtent = useMemo(
    () => collection.extent.spatial.bbox[0],
    [collection]
  );

  // At this time it will be an array of 1
  const accessibilityZoneRef = selectedProduct?.accessibility_zone?.[0];
  const {
    collectionId: accessibilityZoneCollectionId,
    dataset: accessibilityZoneDataset,
  } = accessibilityZoneRef ? parseReference(accessibilityZoneRef) : {};
  const {data: accessibilityZoneData} = useGetCollection(
    {
      collectionId: accessibilityZoneCollectionId,
      dataset: accessibilityZoneDataset,
    },
    {
      enabled: !!accessibilityZoneCollectionId,
    }
  );

  const onClosePopUp = useCallback(() => setPopupPosition(null), []);

  const zoomToExtent = useCallback(extent => {
    const mapView = mapRef.current.getView();

    mapView.fit(extent, {
      padding: [48, 48, 48, 48],
      duration: 500,
    });
  }, []);

  const onLayerClick = (event, features) => {
    const reservationProducts = features
      .map(f => {
        const featureHashId = f.properties_['pl:ref'].split('/').pop();
        const featureReservations = reservations
          .filter(r => r.aoi_ref.endsWith(featureHashId))
          .map(r => r.product_access_id || r.product_id);

        const reservationProducts = products
          .filter(p => featureReservations.includes(p.id))
          .map(p => ({id: p.id, title: p.title}));
        return reservationProducts;
      })
      .flat();

    if (!features.length || !reservationProducts.length) {
      setPopupPosition(null);
      return;
    }

    setSelectedOverlayProducts(reservationProducts);
    setPopupPosition(event.coordinate);
  };

  useEffect(() => {
    if (mapRef?.current) {
      zoomToExtent(collectionExtent);
    }
  }, [mapRef, zoomToExtent, collectionExtent]);

  useEffect(() => {
    if (zoomFeature) {
      zoomToExtent(zoomFeature.bbox);
      if (onZoomToFeature) {
        onZoomToFeature();
      }
    }
  }, [zoomToExtent, zoomFeature, onZoomToFeature]);

  return (
    <>
      <Map
        mapRef={mapRef}
        onZoomToFeatures={() => zoomToExtent(collectionExtent)}
      >
        <CollectionLayer
          collectionId={collection.id}
          token={user.accessToken}
          selectedFeatures={selectedFeatureRefs}
          selectedStyle={selectedStyle}
          style={baseStyle}
          onClick={onLayerClick}
        />
        {!!accessibilityZoneRef && (
          <CollectionLayer
            key={getHashFromId(accessibilityZoneCollectionId)}
            collectionId={getHashFromId(accessibilityZoneCollectionId)}
            token={user.accessToken}
            style={accessibilityZoneStyle}
            dataset={accessibilityZoneDataset}
          />
        )}

        <Overlay
          element={popupElement}
          position={popupPosition}
          positioning="bottom-right"
        />
      </Map>
      <ReservationsOverlay
        innerRef={setPopupElement}
        onClose={onClosePopUp}
        reservations={overlayProducts}
      />
      <Card sx={{position: 'absolute', top: 8, right: 8, width: 250}}>
        <CardHeader
          title="Legend"
          slotProps={{
            title: {
              variant: 'overline',
            },
          }}
          sx={{paddingBlockEnd: 0, paddingBlockStart: 1}}
        />
        <Divider />
        <CardContent sx={{paddingInline: 0}}>
          <List disablePadding>
            {!!accessibilityZoneRef && (
              <ListItem
                secondaryAction={
                  <ZoomToButton
                    tooltipText="Zoom to collection extent"
                    onClick={() => {
                      zoomToExtent(
                        accessibilityZoneData?.extent?.spatial?.bbox[0]
                      );
                    }}
                  />
                }
              >
                <ChipAndLabel
                  color={yellow}
                  sx={{
                    borderStyle: 'dashed',
                    borderColor: yellow[500],
                  }}
                >
                  Accessibility zone
                </ChipAndLabel>
              </ListItem>
            )}
            <ListItem>
              <ChipAndLabel color={pink}>Areas to reserve</ChipAndLabel>
            </ListItem>
            <ListItem>
              <ChipAndLabel color="primary">Reserved areas</ChipAndLabel>
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </>
  );
};

QuotaManagerMap.propTypes = {
  collection: object,
  selectedFeatureRefs: array,
  zoomFeature: object,
  onZoomToFeature: func,
  selectedStyle: oneOfType([array, func, object]),
  baseStyle: oneOfType([array, func, object]),
  reservations: array,
  products: array,
  selectedProduct: object,
};

export default QuotaManagerMap;
